import React, {useEffect, useState} from 'react';
import {Row, Col} from 'react-bootstrap';
import {FmlxButton, FmlxIcon} from 'fmlx-common-ui';
import PropTypes from 'prop-types';
import {setCurrentWizardView} from 'stores/actions/plateTypeEditorStateAction';
import {useDispatch, useSelector} from 'react-redux';
import {selectIsPlateAlreadyTrained, selectIsPlateRecognitionEnabled} from 'stores/selectors/plateRecognitionSelectors';
import {isPlateAlreadyTrained} from 'stores/business/plateRecognitionBusiness';
import withIntl from '../../lib/withIntl';
import FastTextBox from '../../components/Inputs/FastTextBox';
import {Size, TextBoxDecoration, TextBoxMode} from '../../constants/UIComponentEnum';
import {PLATE_PROPERTIES_VALUE_PRECISION} from './PlateTypeEditorEnum';

function SmallTextBox(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <FastTextBox style={{width: '100%'}} size={Size.SM} {...props} />;
}
function SmallVolumeTextBox(props) {
  return (
    <SmallTextBox
      mode={TextBoxMode.NUMBER}
      decoration={TextBoxDecoration.UNIT}
      unit="μL"
      hidesArrowButton
      autoFocus
      maxValue={999999}
      minValue={0}
      inputComponentStyle={{height: '10000px'}}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
    />
  );
}

function WarningMessage({errorMsg}) {
  return (
    <Row>
      <Col sm={12} md={12} className="warning">
        <b>Warning: </b>
        {errorMsg}
      </Col>
    </Row>
  );
}

WarningMessage.propTypes = {errorMsg: PropTypes.string.isRequired};

function PlateViewSummaryDesc({
  intl,
  plateTypeEditorState,
  plateName,
  wellNumber,
  wellBottomProfile,
  wellShape,
  workingVolume,
  capacity,
  onSetCapacity,
  onSetWorkingVolume,
  onSetAllowedToNext,
  onSaveState,
  onSetPlateName,
  isEdit,
  maxCharacters,
  labwareBusinessIsLabwareNameAlreadyExist,
  isEditAllowed,
}) {
  const [isWellCapEdited, setIsWellCapEdited] = useState(false);
  const [isWorkCapEdited, setIsWorkCapEdited] = useState(false);
  const [maxCapacityError, setMaxCapacityError] = useState(false);
  const [maxWorkingCapacityError, setMaxWorkingCapacityError] = useState(false);

  const isPlateRecognitionEnabled = useSelector(selectIsPlateRecognitionEnabled);
  const isPlateTrained = useSelector(selectIsPlateAlreadyTrained);
  const {isReteachingImportedLabware} = plateTypeEditorState;

  const dispatch = useDispatch();

  function onEditWellCapacity() {
    console.log('onEditWellCapacity');
    setIsWellCapEdited(true);
    setIsWorkCapEdited(false);
  }

  function onEditWorkingCapacity() {
    setIsWellCapEdited(false);
    setIsWorkCapEdited(true);
  }

  function onApplyEditWellCap() {
    onSetCapacity(parseFloat(capacity));
    if (capacity >= 0.1) {
      onSetAllowedToNext(true);
    } else {
      onSetAllowedToNext(false);
    }
    setIsWellCapEdited(false);
    onSaveState();
  }

  function onApplyEditWorkCap() {
    onSetWorkingVolume(parseFloat(workingVolume));
    if (workingVolume >= 0.1) {
      onSetAllowedToNext(true);
    } else {
      onSetAllowedToNext(false);
    }
    setIsWorkCapEdited(false);
    onSaveState();
  }

  const {id} = plateTypeEditorState;

  const isNameAlreadyTaken =
    plateName && plateName.trim().length > 0 ? labwareBusinessIsLabwareNameAlreadyExist(plateName, id) : false;

  const validateMaxCapacity = (value) => {
    setMaxWorkingCapacityError(false);

    if (value < workingVolume) {
      setMaxCapacityError(true);

      return;
    }

    setMaxCapacityError(false);
  };

  const validateMaxWorkingCapacity = (value) => {
    setMaxCapacityError(false);

    if (capacity < value) {
      setMaxWorkingCapacityError(true);

      return;
    }

    setMaxWorkingCapacityError(false);
  };

  const renderErrorMessage = (volume, maxErrorFlag) => {
    let errorMsg = '';

    if (volume < 0.1) {
      errorMsg = intl.formatMessage({id: 'labwareManager.edit.warning.minVolume'});
    } else if (maxErrorFlag) {
      errorMsg = intl.formatMessage({id: 'labwareManager.edit.warning.wellCapacityError'});
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (errorMsg === '') return <></>;

    return <WarningMessage errorMsg={errorMsg} />;
  };

  useEffect(() => {
    const checkIsPlateAlreadyTrained = async () => {
      await dispatch(isPlateAlreadyTrained());
    };

    checkIsPlateAlreadyTrained();
  }, [dispatch]);

  return (
    <div className="info-desc-container">
      <div className="section-title bordered">Information & Description</div>
      {!isEdit && (
        <div className="section-additional-info italic">
          Follow the instruction below to measure the well measurement detail.
        </div>
      )}
      <div className="info-desc-content">
        <Row>
          <Col sm={5} md={5} lg={5} xl={5}>
            Plate Name:
          </Col>
          <Col sm={7} md={7} lg={7} xl={7} className="break-word">
            {!isEdit ? (
              <span>{plateName}</span>
            ) : (
              <div>
                <SmallTextBox
                  className="input-plate-info"
                  placeholder="Specify plate name..."
                  value={plateName}
                  onChange={onSetPlateName}
                  onBlur={onSaveState}
                  maxLength={maxCharacters}
                  disabled={!isEditAllowed}
                />
                {isNameAlreadyTaken && (
                  <span className="warning">The name is already taken, please specify another name.</span>
                )}
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col sm={5} md={5} lg={5} xl={5}>
            Number of wells:
          </Col>
          <Col sm={7} md={7} lg={7} xl={7}>
            {wellNumber}
          </Col>
        </Row>
        <Row>
          <Col sm={5} md={5} lg={5} xl={5}>
            Well Bottom Profile:
          </Col>
          <Col sm={7} md={7} lg={7} xl={7}>
            {wellBottomProfile}
          </Col>
        </Row>
        <Row>
          <Col sm={5} md={5} lg={5} xl={5}>
            Well Shape:
          </Col>
          <Col sm={7} md={7} lg={7} xl={7}>
            {wellShape}
          </Col>
        </Row>
        <Row>
          <Col sm={5} md={5} lg={5} xl={5}>
            Well Capacity:
          </Col>
          <Col sm={7} md={7} lg={7} xl={7}>
            {!isWellCapEdited && !isEdit && (
              <div className="label-with-button">
                <div className="vol-text">{`${capacity} μL`}</div>
                <FmlxButton
                  size="sm"
                  label="EDIT"
                  variant="outline"
                  withIcon="start"
                  icon={<FmlxIcon name="Pencil" />}
                  onClick={onEditWellCapacity}
                />
              </div>
            )}
            {(isWellCapEdited || isEdit) && (
              <SmallVolumeTextBox
                placeholder="well capacity"
                value={capacity}
                onChange={(value) => {
                  onSetCapacity(value);
                  validateMaxCapacity(value);
                }}
                onBlur={onApplyEditWellCap}
                disabled={!isEditAllowed}
                precision={PLATE_PROPERTIES_VALUE_PRECISION}
              />
            )}
          </Col>
        </Row>
        {renderErrorMessage(capacity, maxCapacityError)}
        <Row>
          <Col sm={5} md={5} lg={5} xl={5}>
            Max. Working Volume:
          </Col>
          <Col sm={7} md={7} lg={7} xl={7}>
            {!isWorkCapEdited && !isEdit && (
              <div className="label-with-button">
                <div className="vol-text">{`${workingVolume} μL`}</div>
                <FmlxButton
                  size="sm"
                  label="EDIT"
                  variant="outline"
                  withIcon="start"
                  icon={<FmlxIcon name="Pencil" />}
                  onClick={onEditWorkingCapacity}
                />
              </div>
            )}
            {(isWorkCapEdited || isEdit) && (
              <SmallVolumeTextBox
                placeholder="working volume"
                value={workingVolume}
                onChange={(value) => {
                  onSetWorkingVolume(value);
                  validateMaxWorkingCapacity(value);
                }}
                onBlur={onApplyEditWorkCap}
                disabled={!isEditAllowed}
                precision={PLATE_PROPERTIES_VALUE_PRECISION}
              />
            )}
          </Col>
        </Row>
        {renderErrorMessage(workingVolume, maxWorkingCapacityError)}
      </div>
      {!isEdit && (
        <div className="footer-text">
          To redefine the plate definition, tap or click the corresponding button on top of the page.
        </div>
      )}
      {isEdit && isPlateRecognitionEnabled && (
        <Row style={{display: 'flex'}}>
          <Col sm={5} md={5} lg={5} xl={5}>
            Data Train
          </Col>
          <Col sm={5} md={5} lg={5} xl={5} style={{display: 'flex', gap: '10px', flexGrow: '1'}}>
            {isPlateTrained ? intl.formatMessage({id: 'labwareManager.edit.plate.trained'}) : '-'}
            <FmlxButton
              size="sm"
              disabled={isReteachingImportedLabware === true}
              label={
                isPlateTrained
                  ? intl.formatMessage({id: 'labwareManager.edit.plate.retrainPlate'})
                  : intl.formatMessage({id: 'labwareManager.edit.plate.trainPlate'})
              }
              onClick={() => {
                dispatch(setCurrentWizardView(3));
                onSaveState();
              }}
            />
          </Col>
        </Row>
      )}
    </div>
  );
}

export default withIntl(PlateViewSummaryDesc);

PlateViewSummaryDesc.propTypes = {
  intl: PropTypes.object.isRequired,
  plateTypeEditorState: PropTypes.object.isRequired,
  plateName: PropTypes.string.isRequired,
  wellNumber: PropTypes.number.isRequired,
  wellBottomProfile: PropTypes.string.isRequired,
  wellShape: PropTypes.string.isRequired,
  capacity: PropTypes.number.isRequired,
  workingVolume: PropTypes.number.isRequired,
  onSetCapacity: PropTypes.func.isRequired,
  onSetWorkingVolume: PropTypes.func.isRequired,
  onSetAllowedToNext: PropTypes.func.isRequired,
  onSaveState: PropTypes.func,
  onSetPlateName: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  maxCharacters: PropTypes.number,
  labwareBusinessIsLabwareNameAlreadyExist: PropTypes.func.isRequired,
  isEditAllowed: PropTypes.bool.isRequired,
};

PlateViewSummaryDesc.defaultProps = {
  onSaveState: () => {},
  maxCharacters: 45,
};
